import { useState } from 'react';
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  ListItem,
  Stack,
  Text,
  UnorderedList,
  useToast,
} from '@chakra-ui/react';
import { Download, ShieldCheck, Trash2 } from 'lucide-react';
import { getDefaultStore } from 'jotai';
import { useNavigate } from 'react-router-dom';
import { familyNameMatches } from '@twinden/shared';
import { ConfirmSheet } from '../ConfirmSheet';
import { api, ApiError } from '../../lib/api';
import { confirmItsYou, deviceErrorMessage } from '../../lib/devices';
import {
  applyFamilyDeleted,
  markDeletingHere,
  unmarkDeletingHere,
} from '../../hooks/useFamilyDeleted';
import { useT } from '../../i18n';
import { currentUserAtom, familiesAtom } from '../../store';

interface DeleteFamilySheetProps {
  isOpen: boolean;
  onClose: () => void;
  familyId: string;
  familyName: string;
  /** Shown so same-named families can't be mixed up (D217); the typed
   *  confirmation stays the name (D188). */
  familyHandle?: string;
  /** "Download first" (D194): the export, instead of going on. */
  onDownloadFirst: () => void;
}

// Delete family (M54, D188): what goes and that it can't be undone, with
// "Download first" (D194); then a fresh passkey check (D91); then the
// family's name typed exactly; then Delete family.
export function DeleteFamilySheet({
  isOpen,
  onClose,
  familyId,
  familyName,
  familyHandle,
  onDownloadFirst,
}: DeleteFamilySheetProps) {
  const t = useT();
  const toast = useToast();
  const navigate = useNavigate();
  // The passkey check, once done; the last step sends it with the name.
  const [verification, setVerification] = useState<unknown>(null);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setVerification(null);
    setTyped('');
  };

  const close = () => {
    if (busy) return;
    reset();
    onClose();
  };

  const showError = (err: unknown) => {
    const message =
      err instanceof ApiError ? err.message : deviceErrorMessage(err);
    if (message) toast({ title: message, status: 'error' });
  };

  const check = async () => {
    setBusy(true);
    try {
      setVerification(await confirmItsYou());
    } catch (err) {
      showError(err);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    markDeletingHere(familyId);
    try {
      await api.deleteFamily(familyId, verification, typed);
    } catch (err) {
      unmarkDeletingHere(familyId);
      setBusy(false);
      showError(err);
      // A typo keeps the check (the server looks at the name first);
      // anything else used it up, so start again from it.
      if (!(err instanceof ApiError && err.code === 'NAME_MISMATCH')) reset();
      return;
    }
    // This device forgets it at once, unless the socket event got here
    // first and already did. Either way it's this person's doing: no note.
    const store = getDefaultStore();
    const me = store.get(currentUserAtom)?.id;
    if (store.get(familiesAtom).some((family) => family.id === familyId)) {
      await applyFamilyDeleted(
        { familyId, familyName, deletedBy: me ? { id: me } : undefined },
        store,
        () => undefined
      );
    }
    setBusy(false);
    reset();
    onClose();
    toast({
      title: t('account.deleteFamily.done', { family: familyName }),
      status: 'info',
    });
    navigate('/', { replace: true });
  };

  if (verification) {
    const matches = familyNameMatches(typed, familyName);
    return (
      <ConfirmSheet
        isOpen={isOpen}
        onClose={close}
        centered
        icon={<Trash2 size={28} />}
        title={t('account.deleteFamily.typeTitle')}
        body={
          <Stack spacing={4} textAlign="left">
            <Text>
              {t('account.deleteFamily.typeBody', { family: familyName })}
            </Text>
            <Text
              fontWeight={700}
              color="ink"
              bg="surface.subtle"
              borderRadius="12px"
              px={3}
              py={2}
              userSelect="all"
            >
              {familyName}
            </Text>
            {familyHandle && (
              <Text color="muted" fontSize="sm" mt={-2}>
                @{familyHandle}
              </Text>
            )}
            <FormControl>
              <FormLabel fontSize="sm">
                {t('account.deleteFamily.nameLabel')}
              </FormLabel>
              <Input
                h="52px"
                value={typed}
                autoComplete="off"
                autoCapitalize="off"
                spellCheck={false}
                onChange={(event) => setTyped(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && matches) void remove();
                }}
              />
            </FormControl>
          </Stack>
        }
        confirmLabel={t('account.deleteFamily.confirm')}
        confirmIcon={<Trash2 size={18} />}
        confirmDisabled={!matches}
        cancelLabel={t('account.deleteFamily.keep')}
        onConfirm={() => void remove()}
        isLoading={busy}
      />
    );
  }

  return (
    <ConfirmSheet
      isOpen={isOpen}
      onClose={close}
      icon={<Trash2 size={28} />}
      title={t('account.deleteFamily.title', { family: familyName })}
      body={
        <Stack spacing={3} textAlign="left">
          {familyHandle && (
            <Text color="muted" fontWeight={600}>
              @{familyHandle}
            </Text>
          )}
          <Text>{t('account.deleteFamily.goes')}</Text>
          <UnorderedList spacing={2} ml={5}>
            <ListItem>{t('account.deleteFamily.chats')}</ListItem>
            <ListItem>{t('account.deleteFamily.apps')}</ListItem>
            <ListItem>{t('account.deleteFamily.people')}</ListItem>
            <ListItem>{t('account.deleteFamily.invites')}</ListItem>
          </UnorderedList>
          <Text fontWeight={700}>{t('account.deleteFamily.intro')}</Text>
        </Stack>
      }
      confirmLabel={t('account.deleteFamily.continue')}
      confirmIcon={<ShieldCheck size={18} />}
      extra={
        <Button
          h="56px"
          fontSize="17px"
          variant="outline"
          colorScheme="gray"
          borderColor="line"
          bg="surface"
          leftIcon={<Download size={18} />}
          onClick={() => {
            if (busy) return;
            reset();
            onDownloadFirst();
          }}
        >
          {t('account.deleteFamily.downloadFirst')}
        </Button>
      }
      cancelLabel={t('account.deleteFamily.keep')}
      onConfirm={() => void check()}
      isLoading={busy}
    />
  );
}
